// PDF page export utility with annotation rendering
// Exports PDF pages as PNG images including visible annotations

import { Annotation, ANNOTATION_COLORS } from '../types/annotation';

/**
 * Export a PDF page as PNG image with annotations
 * @param pdfDocument - PDF.js document instance (PDFDocumentProxy)
 * @param pageNumber - Page number to export (1-indexed)
 * @param annotations - Annotations on this page to render
 * @param scale - Resolution multiplier (default 2.0 for high resolution)
 * @returns PNG Blob
 */
export async function exportPageWithAnnotations(
  pdfDocument: any,
  pageNumber: number,
  annotations: Annotation[],
  scale: number = 2.0
): Promise<Blob> {
  try {
    // Get the PDF page
    const page = await pdfDocument.getPage(pageNumber);

    // Calculate viewport with scale
    const viewport = page.getViewport({ scale });

    // Create canvas for rendering
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Failed to get canvas 2D context');
    }

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Render PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    // Render annotations on top of the PDF page
    renderAnnotationsToCanvas(
      context,
      annotations,
      scale,
      viewport.height
    );

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      }, 'image/png');
    });
  } catch (error) {
    console.error('Error exporting page with annotations:', error);
    throw error;
  }
}

/**
 * Render annotations onto a canvas context
 * Handles coordinate transformation from PDF space (bottom-left origin) to canvas space (top-left origin)
 * @param ctx - Canvas 2D rendering context
 * @param annotations - Array of annotations to render
 * @param scale - Current scale factor
 * @param pageHeight - Height of the page in canvas coordinates
 */
function renderAnnotationsToCanvas(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  scale: number,
  pageHeight: number
): void {
  annotations.forEach((annotation) => {
    // Skip general notes - they have no position to render on the page
    if (annotation.type === 'general' || !annotation.position) return;

    const color = annotation.color || ANNOTATION_COLORS.yellow;

    if (annotation.type === 'text') {
      // Render text highlights - draw rectangles for each text selection
      annotation.position.rects.forEach((rect) => {
        // Convert PDF coordinates (bottom-left origin) to canvas coordinates (top-left origin)
        const canvasY1 = pageHeight - (rect.y2 * scale);
        const canvasY2 = pageHeight - (rect.y1 * scale);
        const canvasX1 = rect.x1 * scale;
        const canvasX2 = rect.x2 * scale;

        // Draw semi-transparent highlight rectangle
        ctx.fillStyle = hexToRgba(color, 0.3);
        ctx.fillRect(
          canvasX1,
          canvasY1,
          canvasX2 - canvasX1,
          canvasY2 - canvasY1
        );

        // Optional: Add a subtle border
        ctx.strokeStyle = hexToRgba(color, 0.6);
        ctx.lineWidth = 1;
        ctx.strokeRect(
          canvasX1,
          canvasY1,
          canvasX2 - canvasX1,
          canvasY2 - canvasY1
        );
      });
    } else if (annotation.type === 'area') {
      // Render area annotations - draw bounding box with fill
      const boundingRect = annotation.position.boundingRect;

      // Convert PDF coordinates to canvas coordinates
      const canvasY1 = pageHeight - (boundingRect.y2 * scale);
      const canvasX1 = boundingRect.x1 * scale;
      const width = boundingRect.width * scale;
      const height = boundingRect.height * scale;

      // Draw semi-transparent fill
      ctx.fillStyle = hexToRgba(color, 0.2);
      ctx.fillRect(canvasX1, canvasY1, width, height);

      // Draw colored border
      ctx.strokeStyle = color;
      ctx.lineWidth = 2 * scale; // Scale the border width
      ctx.strokeRect(canvasX1, canvasY1, width, height);
    }
  });
}

/**
 * Convert hex color to rgba string with specified opacity
 * @param hex - Hex color code (e.g., '#FFEB3B')
 * @param alpha - Opacity value (0-1)
 * @returns RGBA color string
 */
function hexToRgba(hex: string, alpha: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Trigger download of a blob as a file
 * @param blob - Blob to download
 * @param filename - Desired filename
 */
export function downloadBlob(blob: Blob, filename: string): void {
  try {
    // Create object URL for the blob
    const url = URL.createObjectURL(blob);

    // Create temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);

    // Revoke object URL after a delay to ensure download started
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error downloading blob:', error);
    throw error;
  }
}

/**
 * Export a single page as PNG and trigger download
 * Convenience function that combines export and download
 * @param pdfDocument - PDF.js document instance
 * @param pageNumber - Page number to export (1-indexed)
 * @param annotations - Annotations on this page
 * @param scale - Resolution multiplier (default 2.0)
 */
export async function exportAndDownloadPage(
  pdfDocument: any,
  pageNumber: number,
  annotations: Annotation[],
  scale: number = 2.0
): Promise<void> {
  try {
    const blob = await exportPageWithAnnotations(
      pdfDocument,
      pageNumber,
      annotations,
      scale
    );

    const filename = `page-${pageNumber}.png`;
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Error exporting and downloading page:', error);
    throw error;
  }
}
